/**
 * GM Player Tools — self-targeting GM cheats as one-click buttons
 * (heal / infinite mana / god / no-cooldowns / level / xp / gold).
 *
 * Pure UI over the existing GM_LOW slash commands (ChatCommandHandler) —
 * identical wire to typing them in chat, so server-side gating + system-
 * message feedback stay authoritative. Toggle STATE (god/mana/cd) lives
 * server-side; the buttons re-send the toggle and the chat echo confirms.
 */
import React, { useState } from 'react';
import {
  getRoom,
  sendSlashCommand,
  useRoomPulse,
  useSelfGmLevel,
  selfCanRun,
  GmLevelChip,
  gmBtnStyle,
  gmSectionTitleStyle,
} from './gmShared';

export const GmPlayerToolsContent: React.FC = () => {
  useRoomPulse();
  const gmLevel = useSelfGmLevel();
  const [amount, setAmount] = useState(100);
  const room = getRoom();
  const self = room?.state?.players?.get?.(room?.sessionId);
  const can = (cmd: string) => !!room && selfCanRun(cmd, gmLevel);

  const toggles: Array<{ label: string; cmd: string; on?: boolean }> = [
    { label: 'God mode', cmd: '/god', on: !!self?.invincible },
    { label: 'Infinite mana', cmd: '/mana infinite', on: !!self?.infiniteMana },
    { label: 'No cooldowns', cmd: '/cd', on: !!self?.noCooldowns },
  ];

  return (
    <div style={{ fontSize: 12, color: '#ecf0f1', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>{self?.heroName ?? 'Not connected'}</strong>
        <GmLevelChip gmLevel={gmLevel} />
      </div>
      {self && (
        <div style={{ fontFamily: 'monospace', fontSize: 11, opacity: 0.7 }}>
          L{self.level ?? 0} · HP {self.health ?? 0}/{self.maxHealth ?? 0} · MP {self.mana ?? 0}/{self.maxMana ?? 0} · {self.gold ?? 0}g
        </div>
      )}

      <div style={gmSectionTitleStyle}>Restore</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button style={gmBtnStyle(can('heal'))} disabled={!can('heal')} onClick={() => sendSlashCommand('/heal')}>Full heal</button>
        <button style={gmBtnStyle(can('mana'))} disabled={!can('mana')} onClick={() => sendSlashCommand('/mana')}>Full mana</button>
      </div>

      <div style={gmSectionTitleStyle}>Toggles (state echoes to chat)</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {toggles.map((t) => (
          <button
            key={t.cmd}
            style={{
              ...gmBtnStyle(can(t.cmd.split(' ')[0].slice(1))),
              ...(t.on ? { borderColor: 'rgba(46, 204, 113, 0.6)', color: '#2ecc71' } : {}),
            }}
            disabled={!can(t.cmd.split(' ')[0].slice(1))}
            onClick={() => sendSlashCommand(t.cmd)}
          >
            {t.on ? '● ' : '○ '}{t.label}
          </button>
        ))}
      </div>

      <div style={gmSectionTitleStyle}>Progression</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={gmBtnStyle(can('levelup'))} disabled={!can('levelup')} onClick={() => sendSlashCommand('/levelup')}>Level +1</button>
        <input
          data-testid="gm-amount"
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(Math.max(1, Number(e.target.value) || 1))}
          style={{ width: 64, background: '#1a2230', color: '#ecf0f1', border: '1px solid rgba(236,240,241,0.25)', borderRadius: 5, padding: '4px 6px', fontSize: 11 }}
        />
        <button style={gmBtnStyle(can('xp'))} disabled={!can('xp')} onClick={() => sendSlashCommand(`/xp ${amount}`)}>+XP</button>
        <button style={gmBtnStyle(can('gold'))} disabled={!can('gold')} onClick={() => sendSlashCommand(`/gold ${amount}`)}>+Gold</button>
      </div>

      <div style={{ marginTop: 8, fontSize: 10, opacity: 0.5 }}>
        Self-targeting cheats are GM 3+ (memory stack grants ADMIN). All commands
        run through the normal chat pipeline — feedback appears as system messages.
      </div>
    </div>
  );
};
